import { Box, Stack, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { PartnerListTop } from "@/features/partner/components/partner-list/PartnerListTop";
import { PartnerListTable } from "@/features/partner/components/partner-list/PartnerListTable";
import { usePartnerList } from "@/api/services/main/partner";
import { ImportPartnerModal } from "@/features/partner/components/partner-import/ImportPartnerModal";
import { useVisible } from "@/hooks/useVisible";
import { useGetNationalityList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { usePagination } from "@/hooks/usePagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pagination } from "@/components/elements/Pagination";
import { Button } from "@/components/elements/Button";
import { IconFileImport, IconPlus } from "@/assets/icons";
import { Link, useSearchParams } from "react-router-dom";
import { divider } from "@/theme/colors";
import { AppRoutes } from "@/routes/config";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { PARTNER_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { START_PAGE } from "@/constants/pagination";
import {
  PARTNER_DISPLAY_COLUMNS_STORAGE_KEY,
  PARTNER_ADVANCED_FILTERS_STORAGE_KEY,
  PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY,
  PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY,
  PARTNER_DEFAULT_SEEDED_STORAGE_KEY,
  DEFAULT_PARTNER_LIST_SORT,
} from "@/features/partner/constants/common";
import {
  TPartnerDisplayColumnSetting,
  TPartnerFilterFormExtended,
  defaultPartnerFilterValues,
  defaultPartnerDisplayColumns,
  isEmptyPartnerFilterValue,
  normalizePartnerDateFields,
  normalizePartnerMultiSelectFields,
  stripEmptyPartnerFilterFields,
  PARTNER_LOCKED_COLUMN_IDS,
} from "@/features/partner/components/partner-list/partner-filter-shared";
import { TGetPartnerListReqDto } from "@/api/services/main/partner/dtos/get-partner-list.dto";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  clearSessionFilterState,
  dropLegacyLocalFilterState,
  readSessionFilterState,
  writeSessionFilterState,
} from "@/utils/session-filter-state";
import dayjs from "dayjs";

export const PartnerListPage = () => {
  const pagReq = usePagination();
  const [, setSearchParams] = useSearchParams();

  useSanitizedPageParam({ basePath: AppRoutes.partnerList });
  const { sortValue } = useSanitizedSortParams({ allowedFields: [...PARTNER_SORTABLE_FIELDS] });

  const [appliedConditionName, setAppliedConditionName] = useState<string>(
    () => readSessionFilterState<string>(PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY) ?? "",
  );
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() =>
    readSessionFilterState<number>(PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY),
  );

  const handleAppliedConditionNameChange = useCallback((name: string) => {
    setAppliedConditionName(name);
    if (name) {
      writeSessionFilterState(PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY, name);
    } else {
      clearSessionFilterState(PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY);
    }
  }, []);

  const handleAppliedConditionIdChange = useCallback((id: number | null) => {
    setAppliedConditionId(id);
    if (id !== null) {
      writeSessionFilterState(PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY, id);
    } else {
      clearSessionFilterState(PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY);
    }
  }, []);

  const [advancedFilters, setAdvancedFilters] = useState<TPartnerFilterFormExtended | null>(() => {
    const saved = readSessionFilterState<Partial<TPartnerFilterFormExtended>>(PARTNER_ADVANCED_FILTERS_STORAGE_KEY);
    if (!saved) return null;
    return {
      ...defaultPartnerFilterValues,
      ...normalizePartnerMultiSelectFields(normalizePartnerDateFields(saved)),
    } as TPartnerFilterFormExtended;
  });

  useEffect(() => {
    dropLegacyLocalFilterState([
      PARTNER_ADVANCED_FILTERS_STORAGE_KEY,
      PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY,
      PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY,
    ]);
  }, []);

  const { data: savedConditionsData } = useSavedConditionList("partner");
  const hasSeededDefault = useRef(readSessionFilterState<boolean>(PARTNER_DEFAULT_SEEDED_STORAGE_KEY) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(PARTNER_DEFAULT_SEEDED_STORAGE_KEY, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    const filters = {
      ...defaultPartnerFilterValues,
      ...normalizePartnerMultiSelectFields(normalizePartnerDateFields(fallback.filters ?? {})),
    } as TPartnerFilterFormExtended;
    setAdvancedFilters(filters);
    writeSessionFilterState(PARTNER_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyPartnerFilterFields(filters));
    setAppliedConditionName(fallback.title);
    writeSessionFilterState(PARTNER_APPLIED_CONDITION_NAME_STORAGE_KEY, fallback.title);
    setAppliedConditionId(Number(fallback.id));
    writeSessionFilterState(PARTNER_APPLIED_CONDITION_ID_STORAGE_KEY, Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultPartnerDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(PARTNER_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(START_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TPartnerFilterFormExtended) => {
      const hasAnyFilter = Object.entries(filters).some(([key, value]) =>
        key === "isOverseas" ? value === true : !isEmptyPartnerFilterValue(value),
      );
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        writeSessionFilterState(PARTNER_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyPartnerFilterFields(filters));
      } else {
        setAdvancedFilters(null);
        clearSessionFilterState(PARTNER_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const [displayColumnSettings, setDisplayColumnSettings] = useState<TPartnerDisplayColumnSetting[] | null>(() => {
    const saved = localStorage.getItem(PARTNER_DISPLAY_COLUMNS_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, PARTNER_LOCKED_COLUMN_IDS),
    [displayColumnSettings],
  );

  const handleApplyDisplaySettings = useCallback((columns: TPartnerDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  const query: TGetPartnerListReqDto = {
    page: pagReq.page,
    perPage: pagReq.perPage,
    sorts: [(sortValue ?? DEFAULT_PARTNER_LIST_SORT) as `${string}|${string}`],
    ...(advancedFilters && {
      ...(advancedFilters.managementNumber?.length ? { managementNumber: advancedFilters.managementNumber } : {}),
      ...(advancedFilters.partnerName?.length ? { partnerName: advancedFilters.partnerName } : {}),
      ...(advancedFilters.nationalityId?.length ? { nationalityId: advancedFilters.nationalityId.map(Number) } : {}),
      ...(advancedFilters.prefecture?.length ? { prefecture: advancedFilters.prefecture } : {}),
      ...(advancedFilters.city?.length ? { city: advancedFilters.city } : {}),
      ...(advancedFilters.isOverseas ? { sendingOrganization: advancedFilters.isOverseas } : {}),
      ...(advancedFilters.location?.length ? { location: advancedFilters.location } : {}),
      ...(advancedFilters.contactPerson?.length ? { contactPerson: advancedFilters.contactPerson } : {}),
      ...(advancedFilters.registrationDateFrom ? { registrationDateFrom: dayjs(advancedFilters.registrationDateFrom).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.registrationDateTo ? { registrationDateTo: dayjs(advancedFilters.registrationDateTo).format("YYYY-MM-DD") } : {}),
    }),
  };

  const { data, isLoading } = usePartnerList(query);
  const partnerImportModal = useVisible();
  const { nationalityOptions, isLoading: isLoadingNationality } = useGetNationalityList();
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");

  return (
    <Stack minHeight="100%">
      <Stack mb={3} justifyContent="space-between" alignItems="center" direction="row" width="100%">
        <Typography variant="head20Bold">パートナー管理</Typography>
        <Box display="flex" gap={2}>
          <Button size="sm" onClick={partnerImportModal.open} variant="outline" startIcon={<IconFileImport width={20} height={20} />}>
            一括インポート
          </Button>
          <Button size="sm" component={Link} to={AppRoutes.partnerCreate} startIcon={<IconPlus width={20} height={20} />}>
            新規パートナー登録
          </Button>
        </Box>
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack>
          <PartnerListTop
            pagination={data?.pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            onApplyDisplaySettings={handleApplyDisplaySettings}
            appliedConditionName={appliedConditionName}
            appliedConditionId={appliedConditionId}
            onAppliedConditionNameChange={handleAppliedConditionNameChange}
            onAppliedConditionIdChange={handleAppliedConditionIdChange}
            currentDisplayColumns={lockedDisplayColumnSettings}
            currentAdvancedFilters={advancedFilters}
          />
          <Stack spacing={3}>
            <PartnerListTable rows={data?.data ?? []} isLoading={isLoading} displayColumnSettings={lockedDisplayColumnSettings} />
            <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
          </Stack>
        </Stack>
      </InnerBox>

      <ImportPartnerModal
        partnerModal={partnerImportModal}
        nationalityOptions={nationalityOptions}
        isLoadingNationality={isLoadingNationality}
        contactMethodOptions={contactMethodOptions ?? []}
      />
    </Stack>
  );
};
